// The face rig of MedSnapp's cast, ported from MedSnapp's website repo (src/data/beat-rig.js: EYES, LID_TRAVEL,
// LOW_TRAVEL, MOUTHS, POSES and EXTRAS, values as given; src/components/BeatRig.astro for the parts). The drawings in
// web/public/cast are that rig rendered in its neutral pose with every style inlined; partsOf() finds the rig's parts in
// such a drawing (anchored on the mouth, the one element with an id of its own) and writeRig() sets a frame on them
// with CSS transforms and opacity only. The mouth is swapped, never morphed. If a drawing does not have the rig's shape,
// nothing is changed and the neutral face stays.
//
// A pose here is a set of numbers (Face), so the puppet (puppet.ts) can blend one pose into the next part by part, and
// layer a blink, a glance and a breath on top, the way the website's GSAP rig does.
import type { CastMood } from './CastMascot';

export const EYES = { l: [78, 92], r: [122, 92], rx: 15.5, ry: 19 } as const;
export const LID_TRAVEL = 38;
export const LOW_TRAVEL = 24;

/** Every mouth is a closed shape (website MOUTHS). */
export const MOUTHS = {
  smile: 'M86 120 Q100 134 114 120 Q100 127 86 120 Z',
  open: 'M85 118 Q100 142 115 118 Q100 122 85 118 Z',
  laugh: 'M79 113 Q100 156 121 113 Q100 121 79 113 Z',
  o: 'M100 115 C108 115 111 122 111 128 C111 135 106 140 100 140 C94 140 89 135 89 128 C89 122 92 115 100 115 Z',
  flat: 'M89 123 Q100 125 111 122 Q100 129 89 123 Z',
  frown: 'M86 131 Q100 115 114 131 Q100 124 86 131 Z',
  cry: 'M83 135 Q100 108 117 135 Q100 130 83 135 Z',
  wobble: 'M84 127 C90 119 94 133 100 126 C106 119 110 133 116 126 L116 129 C110 137 106 123 100 130 C94 137 90 123 84 130 Z',
  smirk: 'M88 124 Q104 133 118 114 Q111 136 88 124 Z',
  grit: 'M83 121 Q100 118 117 121 Q115 135 100 135 Q85 135 83 121 Z',
  tiny: 'M96 126 C96 122 104 122 104 126 C104 130 96 130 96 126 Z',
  snarl: 'M84 132 Q100 117 116 132 Q112 137 100 134 Q88 137 84 132 Z',
} as const;
export type Mouth = keyof typeof MOUTHS;

/** The extras a pose switches on (website EXTRAS, in its order). */
export const EXTRAS = ['lash', 'shut', 'arcs', 'pop', 'shine', 'hearts', 'blush', 'dots', 'question', 'sweat', 'tear', 'vein', 'zz', 'spiral'] as const;
export type Extra = (typeof EXTRAS)[number];

interface Lid { c: number; r: number }
interface Brow { o: number; y: number; r: number }
export interface Pose {
  lidL: Lid; lidR: Lid; low: number;
  pupil: { x: number; y: number; s: number; hide?: boolean };
  browL: Brow; browR: Brow;
  mouth: Mouth; tongue: boolean; arms: readonly [number, number];
  body: { sx: number; sy: number; r: number };
  x: readonly Extra[];
}
const P = (o: Partial<Pose>): Pose => ({
  lidL: { c: 0.04, r: 0 }, lidR: { c: 0.04, r: 0 }, low: 0,
  pupil: { x: 1, y: 2, s: 1, hide: false },
  browL: { o: 0, y: 0, r: 0 }, browR: { o: 0, y: 0, r: 0 },
  mouth: 'smile', tongue: false, arms: [18, -18], body: { sx: 1, sy: 1, r: 0 }, x: [],
  ...o,
});
const SHUT: Lid = { c: 1, r: 0 };

/** The website rig's twenty poses (beat-rig.js POSES, values as given), then the one pose of ours an act needs. */
export const POSES = {
  neutral: P({}),
  happy: P({ low: 0.38, pupil: { x: 0, y: 0, s: 1 }, mouth: 'open', tongue: true, arms: [150, -150] }),
  joy: P({ lidL: SHUT, lidR: SHUT, mouth: 'laugh', tongue: true, arms: [168, -168], body: { sx: 0.96, sy: 1.05, r: 0 }, x: ['arcs', 'pop'] }),
  love: P({ low: 0.18, pupil: { x: 0, y: 1, s: 1.3 }, mouth: 'open', tongue: true, arms: [-38, 38], body: { sx: 1, sy: 1, r: -4 }, x: ['shine', 'hearts'] }),
  proud: P({ lidL: { c: 0.38, r: -4 }, lidR: { c: 0.38, r: 4 }, low: 0.22, pupil: { x: 0, y: 1, s: 1 }, mouth: 'smirk', browL: { o: 1, y: -2, r: -8 }, browR: { o: 1, y: -2, r: 8 }, arms: [-28, 28], body: { sx: 0.98, sy: 1.04, r: 0 }, x: ['pop'] }),
  wink: P({ lidL: SHUT, low: 0.3, mouth: 'smirk', browR: { o: 1, y: -6, r: 6 }, arms: [18, -128], x: ['lash'] }),
  relieved: P({ lidL: SHUT, lidR: SHUT, mouth: 'smile', browL: { o: 1, y: -4, r: -10 }, browR: { o: 1, y: -4, r: 10 }, arms: [8, -8], body: { sx: 1.03, sy: 0.96, r: 0 }, x: ['shut', 'sweat'] }),
  shy: P({ lidL: { c: 0.24, r: 0 }, lidR: { c: 0.24, r: 0 }, low: 0.3, pupil: { x: -5, y: 4, s: 0.95 }, mouth: 'tiny', arms: [-34, 34], body: { sx: 1, sy: 0.98, r: -5 }, x: ['blush'] }),
  think: P({ lidL: { c: 0.2, r: 0 }, lidR: { c: 0.2, r: 0 }, pupil: { x: -4, y: -6, s: 1 }, browR: { o: 1, y: -7, r: -10 }, mouth: 'flat', arms: [18, -152], body: { sx: 1, sy: 1, r: -3 }, x: ['dots'] }),
  focused: P({ lidL: { c: 0.3, r: 5 }, lidR: { c: 0.3, r: -5 }, pupil: { x: 0, y: 3, s: 0.84 }, browL: { o: 1, y: 2, r: 8 }, browR: { o: 1, y: 2, r: -8 }, mouth: 'flat', arms: [36, -36] }),
  skeptical: P({ lidL: { c: 0.12, r: 0 }, lidR: { c: 0.48, r: -4 }, pupil: { x: 4, y: 1, s: 0.92 }, browL: { o: 1, y: -10, r: -6 }, browR: { o: 1, y: 5, r: 6 }, mouth: 'flat', arms: [-12, 12], body: { sx: 1, sy: 1, r: 4 } }),
  confused: P({ lidL: { c: 0.08, r: 0 }, lidR: { c: 0.32, r: 6 }, pupil: { x: 2, y: -2, s: 0.9 }, browL: { o: 1, y: -9, r: 10 }, browR: { o: 1, y: 2, r: 14 }, mouth: 'wobble', arms: [40, -18], body: { sx: 1, sy: 1, r: -7 }, x: ['question'] }),
  surprised: P({ lidL: { c: 0, r: 0 }, lidR: { c: 0, r: 0 }, pupil: { x: 0, y: 0, s: 0.62 }, browL: { o: 1, y: -11, r: 0 }, browR: { o: 1, y: -11, r: 0 }, mouth: 'o', arms: [62, -62], body: { sx: 0.95, sy: 1.07, r: 0 } }),
  nervous: P({ lidL: { c: 0.16, r: -6 }, lidR: { c: 0.16, r: 6 }, low: 0.1, pupil: { x: 3, y: 1, s: 0.82 }, browL: { o: 1, y: -5, r: -12 }, browR: { o: 1, y: -5, r: 12 }, mouth: 'wobble', arms: [-26, 26], x: ['sweat'] }),
  sad: P({ lidL: { c: 0.36, r: -15 }, lidR: { c: 0.36, r: 15 }, pupil: { x: 0, y: 5, s: 1 }, browL: { o: 1, y: -2, r: -15 }, browR: { o: 1, y: -2, r: 15 }, mouth: 'frown', arms: [4, -4], body: { sx: 1.03, sy: 0.95, r: 0 } }),
  crying: P({ lidL: { c: 0.42, r: -15 }, lidR: { c: 0.42, r: 15 }, low: 0.2, pupil: { x: 0, y: 4, s: 1 }, browL: { o: 1, y: -3, r: -17 }, browR: { o: 1, y: -3, r: 17 }, mouth: 'cry', tongue: true, arms: [-30, 30], body: { sx: 1.04, sy: 0.94, r: 0 }, x: ['tear'] }),
  angry: P({ lidL: { c: 0.34, r: 20 }, lidR: { c: 0.34, r: -20 }, pupil: { x: 0, y: 2, s: 0.78 }, browL: { o: 1, y: 7, r: 22 }, browR: { o: 1, y: 7, r: -22 }, mouth: 'snarl', arms: [28, -28], body: { sx: 1.05, sy: 0.96, r: 0 }, x: ['vein'] }),
  determined: P({ lidL: { c: 0.3, r: 17 }, lidR: { c: 0.3, r: -17 }, low: 0.16, pupil: { x: 0, y: 1, s: 1 }, browL: { o: 1, y: 4, r: 17 }, browR: { o: 1, y: 4, r: -17 }, mouth: 'grit', arms: [74, -74], body: { sx: 1.04, sy: 0.97, r: 0 } }),
  sleepy: P({ lidL: SHUT, lidR: SHUT, mouth: 'tiny', arms: [4, -4], body: { sx: 1, sy: 0.98, r: 5 }, x: ['shut', 'zz'] }),
  dizzy: P({ pupil: { x: 0, y: 0, s: 1, hide: true }, mouth: 'wobble', arms: [54, -26], body: { sx: 1, sy: 1, r: -8 }, x: ['spiral'] }),
  // Ours (4 Nov 2026), for the yawn: eyes shut, mouth wide, arms stretched up. Not on the website.
  yawn: P({ lidL: SHUT, lidR: SHUT, mouth: 'o', arms: [150, -150], body: { sx: 0.96, sy: 1.07, r: 0 }, x: ['shut'] }),
} satisfies Record<string, Pose>;
export type PoseName = keyof typeof POSES;

/** The website rig's own poses, in its order; everything else in POSES is ours. */
export const SITE_POSES = [
  'neutral', 'happy', 'joy', 'love', 'proud', 'wink', 'relieved', 'shy', 'think', 'focused', 'skeptical', 'confused',
  'surprised', 'nervous', 'sad', 'crying', 'angry', 'determined', 'sleepy', 'dizzy',
] as const satisfies readonly PoseName[];

/** Each mood's pose: right joy, wrong sad, partial surprised, none sleepy, podium proud, idle neutral. */
export const MOOD_POSE: Readonly<Record<CastMood, PoseName>> = { right: 'joy', wrong: 'sad', partial: 'surprised', none: 'sleepy', podium: 'proud', idle: 'neutral' };

// ---------- a pose as numbers ----------

/** A face (and body squash) as numbers, so two can be blended. */
export interface Face {
  /** closed, tilt for the left lid, then the right */
  lid: [number, number, number, number];
  low: number;
  /** x, y, scale, visible (0..1) */
  pupil: [number, number, number, number];
  /** opacity, y, tilt for the left brow, then the right */
  brow: [number, number, number, number, number, number];
  mouth: Mouth;
  tongue: number;
  /** sx, sy, tilt */
  body: [number, number, number];
  /** one opacity per extra, in EXTRAS order */
  x: number[];
}

export function faceOf(name: PoseName): Face {
  const p: Pose = POSES[name];
  return {
    lid: [p.lidL.c, p.lidL.r, p.lidR.c, p.lidR.r],
    low: p.low,
    pupil: [p.pupil.x, p.pupil.y, p.pupil.s, p.pupil.hide ? 0 : 1],
    brow: [p.browL.o, p.browL.y, p.browL.r, p.browR.o, p.browR.y, p.browR.r],
    mouth: p.mouth,
    tongue: p.tongue ? 1 : 0,
    body: [p.body.sx, p.body.sy, p.body.r],
    x: EXTRAS.map((e) => (p.x.includes(e) ? 1 : 0)),
  };
}

const lerp = (a: number, b: number, k: number) => a + (b - a) * k;
const lerpAll = <T extends number[]>(a: T, b: T, k: number): T => a.map((v, i) => lerp(v, b[i], k)) as T;

/** a blended into b by k (0..1); the mouth swaps halfway. */
export function mixFace(a: Face, b: Face, k: number): Face {
  if (k <= 0) return a;
  if (k >= 1) return b;
  return {
    lid: lerpAll(a.lid, b.lid, k), low: lerp(a.low, b.low, k), pupil: lerpAll(a.pupil, b.pupil, k), brow: lerpAll(a.brow, b.brow, k),
    mouth: k < 0.5 ? a.mouth : b.mouth, tongue: lerp(a.tongue, b.tongue, k), body: lerpAll(a.body, b.body, k), x: lerpAll(a.x, b.x, k),
  };
}

// ---------- the parts in a drawing ----------

// The face group's children, in the rig's order (BeatRig.astro), around the mouth.
const BEFORE_MOUTH = ['lash', 'shut', 'arcs'] as const;          // the three just before the mouth
const AFTER_MOUTH = ['tongue', 'pop', 'hearts', 'blush', 'dots', 'question', 'sweat', 'tear', 'vein', 'zz'] as const;

const rotateOf = (el: Element | null): [number, number] | undefined => {
  const m = el?.getAttribute('transform')?.match(/rotate\(\s*-?[\d.]+\s+(-?[\d.]+)\s+(-?[\d.]+)\s*\)/);
  return m ? [Number(m[1]), Number(m[2])] : undefined;
};

export interface Eye { look?: Element; pupil?: Element; shine?: Element; spiral?: Element; lid?: Element; blink?: Element; low?: Element }
export interface RigParts {
  /** the shadow on the ground (website r-ground), outside the character */
  ground?: Element;
  /** the whole character (website r-air): heartbeat and nothing else */
  air: Element;
  /** squash, stretch and tilt about the feet (website r-root) */
  root: Element;
  arms: [Element, Element];
  shoulders: [[number, number], [number, number]];
  browL: Element; browR: Element;
  eyeL: Eye; eyeR: Eye;
  mouth: Element;
  tongue?: Element;
  /** every extra; shine and spiral are one per eye */
  extras: Record<Extra, Element[]>;
}

/** The rig's parts in a drawing, or undefined if the drawing does not have the rig's shape. Changes nothing. */
export function partsOf(svg: SVGSVGElement): RigParts | undefined {
  const mouth = svg.querySelector('path[id$="-mouth"]');
  const face = mouth?.parentElement;
  if (!mouth || !face) return undefined;
  const kids = [...face.children];
  const m = kids.indexOf(mouth);
  if (m !== 7 || kids.length !== m + 1 + AFTER_MOUTH.length) return undefined;
  const seat = face.parentElement; const root = seat?.parentElement; const air = root?.parentElement;
  if (!root || !air) return undefined;
  const arms: [Element, Element] = [root.children[0], root.children[1]];
  const shoulders = arms.map(rotateOf);
  if (!shoulders[0] || !shoulders[1]) return undefined;
  const eye = (g: Element): Eye => {
    const clip = g.children[0];
    const look = clip?.children[1]; const lid = clip?.children[3];
    const pupil = look?.children[0];
    return { look, pupil, shine: pupil?.children[2], spiral: clip?.children[2], lid, blink: lid?.children[0], low: clip?.children[4] };
  };
  const eyeL = eye(kids[2]); const eyeR = eye(kids[3]);
  const named: Partial<Record<string, Element>> = {};
  BEFORE_MOUTH.forEach((n, i) => { named[n] = kids[m - 3 + i]; });
  AFTER_MOUTH.forEach((n, i) => { named[n] = kids[m + 1 + i]; });
  const per = (e: Extra): (Element | undefined)[] => (e === 'shine' ? [eyeL.shine, eyeR.shine] : e === 'spiral' ? [eyeL.spiral, eyeR.spiral] : [named[e]]);
  const extras = Object.fromEntries(EXTRAS.map((e) => [e, per(e).filter((x): x is Element => x instanceof Element)])) as Record<Extra, Element[]>;
  const ground = air.previousElementSibling?.tagName.toLowerCase() === 'ellipse' ? air.previousElementSibling : undefined;
  return {
    ground, air, root, arms, shoulders: shoulders as [[number, number], [number, number]],
    browL: kids[0], browR: kids[1], eyeL, eyeR, mouth, tongue: named.tongue?.firstElementChild ?? undefined, extras,
  };
}

// ---------- writing a frame ----------

/** Everything the rig shows at one moment: a face plus what the puppet layers on top. */
export interface RigFrame {
  face: Face;
  /** the arms, rotation in degrees (0 = hanging; left +ve swings out, right -ve) */
  arms: readonly [number, number];
  /** 1 where an arm is drawn in front of the face (a hand to the chin) */
  front?: readonly [number, number];
  /** 0 open .. 1 shut, on top of the lids */
  blink?: number;
  /** where the eyes glance, in the drawing's units */
  glance?: readonly [number, number];
  /** extra squash on the body (breath): multiplies sx, sy */
  breathe?: readonly [number, number];
  /** the whole character's scale (Beat's heartbeat) */
  beat?: readonly [number, number];
  /** ms, for the extras that loop (tears, the dizzy spiral, the zz, hearts) */
  t?: number;
}

const r2 = (n: number) => Math.round(n * 100) / 100;
const px = (n: number) => `${r2(n)}px`;
const about = (cx: number, cy: number, inner: string) => `translate(${px(cx)}, ${px(cy)}) ${inner} translate(${px(-cx)}, ${px(-cy)})`;

/** Writes styles only when they change. One per drawing. */
export class StyleWriter {
  private t = new Map<Element, string>();
  private o = new Map<Element, string>();
  private d = new Map<Element, string>();
  tf(el: Element | null | undefined, v: string) {
    if (!(el instanceof SVGElement || el instanceof HTMLElement) || this.t.get(el) === v) return;
    this.t.set(el, v); el.style.transform = v;
  }
  op(el: Element | null | undefined, v: number) {
    if (!(el instanceof SVGElement || el instanceof HTMLElement)) return;
    const s = String(r2(v));
    if (this.o.get(el) === s) return;
    this.o.set(el, s); el.style.opacity = s;
  }
  path(el: Element, v: string) {
    if (this.d.get(el) === v) return;
    this.d.set(el, v); el.setAttribute('d', v);
  }
}

/** Front copies of the arms (for a hand in front of the face), made once per drawing by the puppet. */
export type FrontArms = [Element | undefined, Element | undefined];

const TEAR_MS = 1100;

/** Sets a frame on a drawing's parts. Transforms and opacity only; the mouth's path is swapped. */
export function writeRig(parts: RigParts, f: RigFrame, w: StyleWriter, frontArms: FrontArms = [undefined, undefined]): void {
  const { face } = f;
  const [bsx, bsy] = f.breathe ?? [1, 1];
  const [sx, sy, r] = face.body;
  w.tf(parts.root, `translate(100px, 180px) rotate(${r2(r)}deg) scale(${r2(sx * bsx * 1000) / 1000}, ${r2(sy * bsy * 1000) / 1000}) translate(-100px, -180px)`);
  const [ax, ay] = f.beat ?? [1, 1];
  w.tf(parts.air, ax === 1 && ay === 1 ? 'none' : `translate(100px, 180px) scale(${r2(ax * 1000) / 1000}, ${r2(ay * 1000) / 1000}) translate(-100px, -180px)`);
  parts.arms.forEach((a, i) => {
    const [cx, cy] = parts.shoulders[i];
    const t = about(cx, cy, `rotate(${r2(f.arms[i])}deg)`);
    const front = f.front?.[i] ?? 0;
    const copy = frontArms[i];
    w.tf(a, t); w.op(a, copy ? 1 - front : 1);
    if (copy) { w.tf(copy, t); w.op(copy, front); }
  });
  const [oL, yL, rL, oR, yR, rR] = face.brow;
  w.tf(parts.browL, `translate(0px, ${px(yL)}) ${about(EYES.l[0], 66, `rotate(${r2(rL)}deg)`)}`); w.op(parts.browL, oL);
  w.tf(parts.browR, `translate(0px, ${px(yR)}) ${about(EYES.r[0], 66, `rotate(${r2(rR)}deg)`)}`); w.op(parts.browR, oR);
  const [gx, gy] = f.glance ?? [0, 0];
  const [pxx, pyy, ps, pv] = face.pupil;
  const blink = f.blink ?? 0;
  for (const [eye, c, lr, at] of [[parts.eyeL, face.lid[0], face.lid[1], EYES.l], [parts.eyeR, face.lid[2], face.lid[3], EYES.r]] as const) {
    const [cx, cy] = at;
    w.op(eye.look, pv);
    w.tf(eye.look, gx === 0 && gy === 0 ? 'none' : `translate(${px(gx)}, ${px(gy)})`);
    w.tf(eye.pupil, `translate(${px(pxx)}, ${px(pyy)}) ${about(cx, cy, `scale(${r2(ps * 100) / 100})`)}`);
    w.tf(eye.lid, `translate(0px, ${px(c * LID_TRAVEL)}) ${about(cx, cy, `rotate(${r2(lr)}deg)`)}`);
    w.tf(eye.blink, `translate(0px, ${px(blink * Math.max(0, 1 - c) * LID_TRAVEL)})`);
    w.tf(eye.low, `translate(0px, ${px(-face.low * LOW_TRAVEL)})`);
  }
  w.path(parts.mouth, MOUTHS[face.mouth]);
  w.op(parts.tongue, face.tongue);
  const t = f.t ?? 0;
  EXTRAS.forEach((name, i) => {
    const o = face.x[i];
    const pop = o > 0 && o < 1 ? 0.4 + 0.6 * o : 1;
    for (const el of parts.extras[name]) {
      w.op(el, o);
      if (f.t === undefined) continue; // a still pose: opacity only, as the drawing came
      let tf = pop === 1 ? 'none' : `scale(${r2(pop)})`;
      if (o > 0) {
        if (name === 'spiral') tf = `rotate(${Math.round((t * 0.3) % 360)}deg) scale(${r2(pop)})`;
        else if (name === 'hearts') tf = `scale(${r2(pop * (1 + 0.08 * Math.sin((t / 700) * Math.PI * 2)))})`;
        else if (name === 'zz') tf = `translate(${px(1.5 * Math.sin(t / 500))}, ${px(-2 - 2 * Math.sin(t / 650))}) scale(${r2(pop)})`;
      }
      w.tf(el, tf);
      if (name === 'tear') {
        [...el.children].forEach((drop, k) => {
          const p = o > 0 ? (t / TEAR_MS + k * 0.45) % 1 : 0;
          w.tf(drop, `translate(0px, ${px(p * 46)})`);
          w.op(drop, o > 0 ? (p < 0.15 ? p / 0.15 : 1 - (p - 0.15) / 0.85) : 1);
        });
      }
    }
  });
}

/** Sets a pose on a cast drawing that is in the page. Returns false (and changes nothing) if the drawing is not the rig's. */
export function poseCast(svg: SVGSVGElement, name: PoseName): boolean {
  const parts = partsOf(svg);
  if (!parts) return false;
  writeRig(parts, { face: faceOf(name), arms: POSES[name].arms }, new StyleWriter());
  return true;
}

/** A drawing's text with its ids made unique, so two drawings on one page never share a clip path. */
export function uniqueIds(svgText: string, uid: string): string {
  const prefix = svgText.match(/id="([A-Za-z0-9_]+)-b"/)?.[1];
  return prefix ? svgText.split(prefix).join(uid) : svgText;
}
